"use client"
import { useEffect, useState } from "react"

/**
 * Defers non-essential third-party scripts (chat, analytics, form embeds) until the
 * visitor actually does something on the page.
 *
 * Why: every one of these scripts used to load unconditionally at page load, so
 * third-party cookies were set before a visitor had touched
 * anything. That cost us the Lighthouse `third-party-cookies` audit (weight 5 — the
 * single heaviest item in Best Practices) and `inspector-issues` along with it, and it
 * is also the wrong default for a visitor who bounces without interacting.
 *
 * Trigger is interaction-only by design. A timer would re-introduce the same problem for
 * anyone who lands and reads, and would still be captured by an automated audit. Real
 * visitors fire one of these events within moments of engaging; a bot or a bounce does not.
 *
 * `once: true` + `passive: true` so the listeners never affect scroll performance and
 * clean themselves up after the first hit.
 */
// `scroll` and `wheel` were removed deliberately. Scrolling is not intent — it is
// what every reader does, and it was pulling ~520 KB of chat bundle for visitors
// who never wanted to chat. It also fires during a Lighthouse run, which is why
// the audit saw the whole GHL payload on a page that is supposed to defer it.
// pointerdown/keydown/touchstart still catch anyone actually engaging.
const TRIGGERS = ["pointerdown", "keydown", "touchstart"] as const

export function useLazyThirdParty(): boolean {
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (ready) return

    const fire = () => setReady(true)
    for (const evt of TRIGGERS) {
      window.addEventListener(evt, fire, { once: true, passive: true })
    }
    return () => {
      for (const evt of TRIGGERS) window.removeEventListener(evt, fire)
    }
  }, [ready])

  return ready
}
